// Admin Dashboard — Phase 1+ (Overview, Users, System Health, Reports, Contact,
// Videos, Payments, CV Analytics). Permission-aware sidebar based on /api/admin/me.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import BackBar from "@/components/BackBar";
import {
  LayoutDashboard, Users as UsersIcon, AlertTriangle, Mail, Video,
  CreditCard, BarChart3, Activity, Loader2, Search, ShieldCheck, ShieldOff, Trash2,
  Crown, CheckCircle2, XCircle, Plus, Upload, GripVertical, Eye, MousePointer, FileText,
  AlertOctagon, UserCog, UserPlus, ArrowRightLeft, Pin, Clock, X, Image as ImageIcon, Hourglass,
} from "lucide-react";

// ─── format helpers ───
const fmtBytes = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};
const fmtDuration = (s) => {
  if (!s || s <= 0) return "—";
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};
const statusBadgeCls = (s) => ({
  published:        "bg-emerald-500/20 text-emerald-300",
  pending_approval: "bg-amber-500/20 text-amber-300",
  draft:            "bg-slate-800 text-slate-300",
  hidden:           "bg-blue-500/20 text-blue-300",
  rejected:         "bg-red-500/20 text-red-300",
}[s] || "bg-slate-800 text-slate-300");
const statusLabel = (s, t) => ({
  published:        t("منشور",        "Published"),
  pending_approval: t("بانتظار الموافقة", "Pending"),
  draft:            t("مسودة",        "Draft"),
  hidden:           t("مخفي",         "Hidden"),
  rejected:         t("مرفوض",        "Rejected"),
}[s] || s);

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

const TABS = [
  { id: "overview",  ar: "نظرة عامة",     en: "Overview",       Icon: LayoutDashboard, role: "viewer" },
  { id: "users",     ar: "المستخدمون",    en: "Users",          Icon: UsersIcon,        role: "support" },
  { id: "team",      ar: "فريق الإدارة",  en: "Admin Team",     Icon: UserCog,          role: "admin" },
  { id: "reports",   ar: "البلاغات",      en: "Reports",        Icon: AlertTriangle,    role: "support" },
  { id: "moderation",ar: "سجل الإجراءات", en: "Moderation Log", Icon: FileText,         role: "support" },
  { id: "contact",   ar: "رسائل التواصل", en: "Contact",        Icon: Mail,             role: "support" },
  { id: "videos",    ar: "الفيديوهات",    en: "Videos",         Icon: Video,            role: "admin" },
  { id: "media",     ar: "مراجعة الصور",  en: "Media Review",   Icon: ImageIcon,        role: "admin" },
  { id: "payments",  ar: "المدفوعات",     en: "Payments",       Icon: CreditCard,       role: "admin" },
  { id: "analytics", ar: "تحليلات السير", en: "CV Analytics",   Icon: BarChart3,        role: "viewer" },
  { id: "health",    ar: "صحة النظام",    en: "System Health",  Icon: Activity,         role: "viewer" },
];

const ROLE_RANK = { viewer: 0, support: 1, admin: 2, owner: 3 };
const canSee = (myRole, needRole, me, tabId) => {
  // Side-role: video_contributor sees only Videos tab
  if (myRole === "video_contributor") return tabId === "videos";
  return (ROLE_RANK[myRole] ?? -1) >= (ROLE_RANK[needRole] ?? 99);
};

const StatCard = ({ label, value, accent = "from-yellow-500 to-amber-600", testid }) => (
  <div className="glass-card rounded-2xl p-4" data-testid={testid}>
    <div className={`text-xs text-slate-400`}>{label}</div>
    <div className={`text-2xl font-bold mt-1 bg-gradient-to-br ${accent} bg-clip-text text-transparent`}>{value}</div>
  </div>
);

export default function Admin() {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [me, setMe]       = useState(null);
  const [tab, setTab]     = useState("overview");
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);

  // Bootstrap — gate access
  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    axios.get(`${API}/admin/me`, auth(token))
      .then((r) => {
        setMe(r.data);
        // Contributors only see videos — land them there directly
        if (r.data.role === "video_contributor") setTab("videos");
      })
      .catch(() => { toast.error(t("ليس لديك صلاحية إدارية", "You do not have admin access")); navigate("/dashboard"); });
  }, [token, navigate, t]);

  // Load tab data
  useEffect(() => {
    if (!me) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const url = {
          overview:   `${API}/admin/overview`,
          users:      `${API}/admin/users`,
          team:       `${API}/admin/team`,
          reports:    `${API}/admin/reports`,
          moderation: `${API}/admin/actions?limit=200`,
          contact:    `${API}/admin/contact-messages`,
          videos:     `${API}/admin/videos`,
          media:      `${API}/admin/media?status=pending_review`,
          payments:   `${API}/admin/payments`,
          analytics:  `${API}/admin/cv-analytics`,
          health:     `${API}/admin/system-health`,
        }[tab];
        const r = await axios.get(url, auth(token));
        if (mounted) setData(r.data);
      } catch (e) {
        if (mounted) setData(null);
        toast.error(e?.response?.data?.detail || t("فشل التحميل", "Failed to load"));
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [tab, me, token, t]);

  if (!me) return <DashboardLayout title={t("لوحة الإدارة", "Admin Dashboard")}><div className="p-20 flex justify-center"><Loader2 className="animate-spin text-yellow-500" /></div></DashboardLayout>;

  const visibleTabs = TABS.filter((tt) => canSee(me.role, tt.role, me, tt.id));

  return (
    <DashboardLayout title={t("لوحة الإدارة", "Admin Dashboard")}>
      <BackBar to="/dashboard" label_ar="الرئيسية" label_en="Home"
        crumbs={[{ to: "/dashboard", ar: "الرئيسية", en: "Home" }, { ar: "لوحة الإدارة", en: "Admin" }]} />
      <div className="grid lg:grid-cols-[220px_1fr] gap-4" data-testid="admin-page">
        {/* Sidebar */}
        <nav className="glass-card rounded-2xl p-2 flex lg:flex-col gap-1 overflow-x-auto" data-testid="admin-sidebar">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 hidden lg:flex items-center gap-1">
            <Crown size={10} className="text-yellow-500" />
            <span>{me.role}</span>
            <Badge className="bg-slate-800 text-slate-300 text-[10px] ml-auto">{me.email}</Badge>
          </div>
          {visibleTabs.map((tt) => (
            <button key={tt.id} onClick={() => setTab(tt.id)}
              className={`text-sm px-3 py-2 rounded-xl inline-flex items-center gap-2 whitespace-nowrap transition ${tab === tt.id ? "bg-yellow-500 text-black font-semibold" : "text-slate-300 hover:bg-slate-800"}`}
              data-testid={`admin-tab-${tt.id}`}>
              <tt.Icon size={14} />{language === "ar" ? tt.ar : tt.en}
            </button>
          ))}
        </nav>

        {/* Main panel */}
        <div className="space-y-4">
          {loading ? (
            <div className="glass-card rounded-2xl p-10 flex justify-center"><Loader2 className="animate-spin text-yellow-500" /></div>
          ) : tab === "overview"   ? <Overview  d={data} t={t} /> :
              tab === "users"      ? <UsersTab  data={data} me={me} t={t} token={token} reload={() => setTab("users")} /> :
              tab === "team"       ? <TeamTab   data={data} me={me} t={t} token={token} /> :
              tab === "reports"    ? <ReportsTab data={data} t={t} token={token} reload={() => setTab("reports")} /> :
              tab === "moderation" ? <ModerationTab data={data} t={t} /> :
              tab === "contact"    ? <ContactTab data={data} t={t} /> :
              tab === "videos"     ? <VideosTab data={data} me={me} t={t} token={token} reload={() => setTab("videos")} /> :
              tab === "media"      ? <MediaTab data={data} t={t} token={token} reload={async () => { const r = await axios.get(`${API}/admin/media?status=pending_review`, auth(token)); setData(r.data); }} /> :
              tab === "payments"   ? <PaymentsTab data={data} t={t} /> :
              tab === "analytics"  ? <AnalyticsTab data={data} t={t} /> :
              tab === "health"     ? <HealthTab data={data} t={t} /> : null}
        </div>
      </div>
    </DashboardLayout>
  );
}

// ─── Overview ───
function Overview({ d, t }) {
  if (!d) return <div className="text-slate-400">{t("لا توجد بيانات", "No data")}</div>;
  const cards = [
    ["total_users",        t("إجمالي المستخدمين",   "Total users"),        "from-blue-500 to-cyan-600"],
    ["new_users_today",    t("جدد اليوم",            "New today"),          "from-emerald-500 to-teal-600"],
    ["paid_users",         t("المدفوعون",            "Paid users"),         "from-yellow-500 to-amber-600"],
    ["total_revenue",      t("إجمالي الإيرادات ($)","Revenue ($)"),         "from-green-500 to-emerald-700"],
    ["cvs_created",        t("سير ذاتية",           "Resumes created"),     "from-purple-500 to-violet-700"],
    ["pdfs_downloaded",    t("تنزيلات PDF",          "PDF downloads"),       "from-red-500 to-orange-700"],
    ["job_searches",       t("بحوث الوظائف",         "Job searches"),        "from-cyan-500 to-blue-700"],
    ["discover_searches",  t("بحوث Discover",        "Discover searches"),   "from-pink-500 to-rose-700"],
    ["housing_searches",   t("بحوث السكن",           "Housing searches"),    "from-orange-500 to-red-700"],
    ["community_messages", t("رسائل المجتمع",        "Community messages"),  "from-teal-500 to-emerald-700"],
    ["contact_messages",   t("رسائل التواصل",        "Contact messages"),    "from-slate-400 to-slate-600"],
    ["videos_published",   t("فيديوهات منشورة",     "Videos published"),    "from-fuchsia-500 to-purple-700"],
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="overview-cards">
      {cards.map(([k, label, accent]) => <StatCard key={k} label={label} value={d[k] ?? 0} accent={accent} testid={`overview-${k}`} />)}
    </div>
  );
}

// ─── Users ───
function UsersTab({ data, me, t, token, reload }) {
  const [q, setQ] = useState("");
  const [overrideRows, setOverrideRows] = useState(null);
  const [warnTarget, setWarnTarget] = useState(null);
  const [warnReason, setWarnReason] = useState("");
  const rows = overrideRows ?? data?.rows ?? [];
  const search = async () => {
    const r = await axios.get(`${API}/admin/users?q=${encodeURIComponent(q)}`, auth(token));
    setOverrideRows(r.data.rows || []);
  };
  const block = async (uid, blocked) => {
    await axios.post(`${API}/admin/users/${uid}/${blocked ? "unblock" : "block"}`, { note: "" }, auth(token));
    toast.success(t("تم", "Done")); search();
  };
  const remove = async (uid) => {
    if (!window.confirm(t("هل تريد حذف هذا المستخدم نهائياً؟", "Permanently delete this user?"))) return;
    try { await axios.delete(`${API}/admin/users/${uid}`, auth(token)); toast.success(t("تم الحذف", "Deleted")); search(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("فشل", "Failed")); }
  };
  const changeRole = async (uid, role) => {
    try { await axios.put(`${API}/admin/users/${uid}/role`, { role }, auth(token)); toast.success(t("تم تحديث الدور", "Role updated")); search(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("فشل", "Failed")); }
  };
  const sendWarn = async () => {
    if (!warnReason.trim()) { toast.error(t("اكتب سبب التحذير", "Enter a reason")); return; }
    try { await axios.post(`${API}/admin/warn`, { user_id: warnTarget.user_id, reason: warnReason.trim() }, auth(token));
      toast.success(t("تم إرسال التحذير", "Warning sent")); setWarnTarget(null); setWarnReason(""); }
    catch (e) { toast.error(e?.response?.data?.detail || t("فشل", "Failed")); }
  };
  return (
    <div className="space-y-3" data-testid="users-tab">
      <div className="flex gap-2">
        <Input placeholder={t("ابحث بالاسم/الإيميل/اسم المستخدم", "Search name/email/username")} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} className="bg-slate-900 text-white border-slate-700" data-testid="users-search" />
        <Button onClick={search} className="btn-primary" data-testid="users-search-btn"><Search size={14} /></Button>
      </div>
      <div className="glass-card rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/60 text-slate-400 text-[11px]">
            <tr>
              <th className="p-2 text-right">{t("الاسم", "Name")}</th>
              <th className="p-2 text-right">{t("الإيميل", "Email")}</th>
              <th className="p-2">{t("الدور", "Role")}</th>
              <th className="p-2">CVs</th>
              <th className="p-2">{t("الانضمام", "Joined")}</th>
              <th className="p-2">{t("الحالة", "Status")}</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.user_id} className="border-t border-slate-800 hover:bg-slate-900/30" data-testid={`user-row-${u.user_id}`}>
                <td className="p-2 text-white">{u.name}</td>
                <td className="p-2 text-slate-300">{u.email}</td>
                <td className="p-2 text-center">
                  {me.can.manage_admins ? (
                    <select value={u.role || "user"} onChange={(e) => changeRole(u.user_id, e.target.value)} className="bg-slate-800 text-white text-xs rounded px-2 py-1 border border-slate-700" data-testid={`user-role-${u.user_id}`}>
                      <option value="user">user</option><option value="viewer">viewer</option><option value="support">support</option><option value="admin">admin</option><option value="owner">owner</option>
                    </select>
                  ) : <Badge className="bg-slate-800 text-slate-300">{u.role || "user"}</Badge>}
                </td>
                <td className="p-2 text-center text-slate-300">{u.cv_count || 0}</td>
                <td className="p-2 text-center text-slate-400 text-[11px]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="p-2 text-center">{u.blocked ? <Badge className="bg-red-500/20 text-red-300">{t("محظور", "Blocked")}</Badge> : <Badge className="bg-emerald-500/20 text-emerald-300">{t("نشط", "Active")}</Badge>}</td>
                <td className="p-2 text-center flex gap-1 justify-center">
                  <button onClick={() => { setWarnTarget(u); setWarnReason(""); }} className="text-slate-400 hover:text-amber-400 p-1" data-testid={`user-warn-${u.user_id}`} title={t("تحذير", "Warn")}><AlertOctagon size={14} /></button>
                  {me.can.manage_users && (
                    <button onClick={() => block(u.user_id, u.blocked)} className="text-slate-400 hover:text-yellow-400 p-1" data-testid={`user-block-${u.user_id}`} title={u.blocked ? t("رفع الحظر", "Unblock") : t("حظر", "Block")}>
                      {u.blocked ? <ShieldCheck size={14} /> : <ShieldOff size={14} />}
                    </button>
                  )}
                  {me.can.delete_users && (
                    <button onClick={() => remove(u.user_id)} className="text-slate-400 hover:text-red-400 p-1" data-testid={`user-delete-${u.user_id}`} title={t("حذف", "Delete")}><Trash2 size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Warn dialog */}
      {warnTarget && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setWarnTarget(null)} data-testid="warn-modal">
          <div className="glass-card rounded-2xl p-5 max-w-md w-full space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold flex items-center gap-2"><AlertOctagon className="text-amber-400" size={18} />{t("تحذير المستخدم", "Warn user")}</h3>
            <div className="text-sm text-slate-300">{warnTarget.name} · {warnTarget.email}</div>
            <textarea value={warnReason} onChange={(e) => setWarnReason(e.target.value)} rows={3}
              placeholder={t("سبب التحذير (سيتم تسجيله في سجل الإجراءات)", "Reason — saved to moderation log")}
              className="w-full bg-slate-900 text-white border border-slate-700 rounded-lg p-2 text-sm resize-none"
              data-testid="warn-reason" />
            <div className="flex justify-end gap-2">
              <Button onClick={() => setWarnTarget(null)} variant="outline" className="border-slate-700 text-slate-300" data-testid="warn-cancel">{t("إلغاء", "Cancel")}</Button>
              <Button onClick={sendWarn} className="btn-primary" data-testid="warn-send"><AlertOctagon size={12} className="ml-1" />{t("إرسال التحذير", "Send warning")}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Admin Team ───
function TeamTab({ data, me, t, token }) {
  const [rows, setRows] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole,  setInviteRole]  = useState("admin");
  const [activityFor, setActivityFor] = useState(null);     // { user_id, name, activity: [] }
  useEffect(() => { setRows(Array.isArray(data) ? data : []); }, [data]);

  const refresh = async () => {
    const r = await axios.get(`${API}/admin/team`, auth(token));
    setRows(r.data || []);
  };

  const invite = async () => {
    if (!inviteEmail.trim()) { toast.error(t("اكتب البريد الإلكتروني", "Enter an email")); return; }
    try {
      await axios.post(`${API}/admin/team/invite`, { email: inviteEmail.trim(), role: inviteRole }, auth(token));
      toast.success(t("تمت إضافة العضو إلى فريق الإدارة", "Member added to admin team"));
      setInviteEmail("");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || t("فشلت الإضافة", "Invite failed")); }
  };

  const changeRole = async (u, role) => {
    if (!me.can.manage_admins) { toast.error(t("صلاحية المالك فقط", "Owner-only")); return; }
    try { await axios.put(`${API}/admin/users/${u.user_id}/role`, { role }, auth(token)); toast.success(t("تم تحديث الدور", "Role updated")); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("فشل", "Failed")); }
  };

  const remove = async (u) => {
    if (!window.confirm(t(`هل تريد إزالة "${u.name || u.email}" من فريق الإدارة؟`, `Remove "${u.name || u.email}" from the admin team?`))) return;
    try { await axios.delete(`${API}/admin/team/${u.user_id}`, auth(token)); toast.success(t("تمت الإزالة", "Removed")); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("فشل", "Failed")); }
  };

  const transfer = async (u) => {
    if (!window.confirm(t(`سيتم نقل الملكية إلى ${u.email}. ستصبح أنت admin بدلاً من Owner. متابعة؟`, `Ownership will be transferred to ${u.email}. You'll become admin instead of Owner. Continue?`))) return;
    try { await axios.post(`${API}/admin/team/${u.user_id}/transfer-ownership`, {}, auth(token)); toast.success(t("تم نقل الملكية", "Ownership transferred")); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("فشل", "Failed")); }
  };

  const openActivity = async (u) => {
    try {
      const r = await axios.get(`${API}/admin/team/${u.user_id}/activity`, auth(token));
      setActivityFor({ user_id: u.user_id, name: u.name || u.email, rows: r.data || [] });
    } catch { toast.error(t("فشل تحميل النشاط", "Failed to load activity")); }
  };

  const roleBadge = (r) => ({
    owner:   "bg-yellow-500/20 text-yellow-300",
    admin:   "bg-emerald-500/20 text-emerald-300",
    support: "bg-blue-500/20 text-blue-300",
    viewer:  "bg-slate-700 text-slate-300",
  }[r] || "bg-slate-700 text-slate-300");

  return (
    <div className="space-y-4" data-testid="team-tab">
      {/* Invite */}
      {me.can.manage_admins && (
        <div className="glass-card rounded-2xl p-4 space-y-2" data-testid="team-invite-panel">
          <h3 className="text-sm font-bold text-white flex items-center gap-1"><UserPlus size={14} className="text-yellow-500" />{t("دعوة عضو جديد لفريق الإدارة", "Invite a new admin member")}</h3>
          <p className="text-[11px] text-slate-400">{t("ملاحظة: يجب أن يكون المستخدم قد سجّل حساباً مسبقاً عبر صفحة /register، ثم تتم ترقيته من هنا.", "Note: the user must register first via /register, then you can promote them here.")}</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="user@example.com" className="bg-slate-900 text-white border-slate-700 flex-1" data-testid="team-invite-email" />
            <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="bg-slate-900 text-white border border-slate-700 rounded px-2 py-1.5 text-sm" data-testid="team-invite-role">
              <option value="viewer">Viewer</option>
              <option value="support">Support</option>
              <option value="admin">Admin</option>
              <option value="owner">Owner</option>
            </select>
            <Button onClick={invite} className="btn-primary" data-testid="team-invite-btn"><UserPlus size={14} className="ml-1" />{t("إضافة", "Add")}</Button>
          </div>
        </div>
      )}

      {/* Team roster */}
      <div className="glass-card rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/60 text-slate-400 text-[11px]">
            <tr>
              <th className="p-2 text-right">{t("العضو", "Member")}</th>
              <th className="p-2">{t("الدور", "Role")}</th>
              <th className="p-2">{t("آخر تسجيل دخول", "Last login")}</th>
              <th className="p-2">{t("نشاطات", "Actions")}</th>
              <th className="p-2">{t("آخر إجراء", "Last action")}</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="p-6 text-center text-slate-400 text-sm">{t("لا يوجد أعضاء فريق بعد", "No team members yet")}</td></tr>
            ) : rows.map((u) => {
              const isMe       = u.user_id === me.user_id;
              const isLastOwn  = u.role === "owner" && rows.filter((r) => r.role === "owner").length <= 1;
              return (
              <tr key={u.user_id} className="border-t border-slate-800 hover:bg-slate-900/30" data-testid={`team-row-${u.user_id}`}>
                <td className="p-2">
                  <div className="flex items-center gap-2">
                    {u.avatar
                      ? <img src={u.avatar.startsWith("http") ? u.avatar : `${API}/uploads/${u.avatar}`} alt="" className="w-8 h-8 rounded-full object-cover" />
                      : <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-500 to-amber-700 flex items-center justify-center text-white text-xs font-bold">{(u.name || "?").slice(0,1).toUpperCase()}</div>}
                    <div>
                      <div className="text-white font-medium text-sm flex items-center gap-1">{u.name || "—"}{isMe && <Badge className="bg-yellow-500/30 text-yellow-200 text-[9px]">{t("أنت", "you")}</Badge>}</div>
                      <div className="text-[11px] text-slate-400">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td className="p-2 text-center">
                  {me.can.manage_admins && !isMe && !isLastOwn ? (
                    <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} className={`text-xs rounded px-2 py-1 border border-slate-700 bg-slate-800 ${roleBadge(u.role)}`} data-testid={`team-role-${u.user_id}`}>
                      <option value="viewer">viewer</option>
                      <option value="support">support</option>
                      <option value="admin">admin</option>
                      <option value="owner">owner</option>
                    </select>
                  ) : <Badge className={roleBadge(u.role)}>{u.role}</Badge>}
                </td>
                <td className="p-2 text-center text-slate-400 text-[11px]" data-testid={`team-lastlogin-${u.user_id}`}>{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}</td>
                <td className="p-2 text-center"><Badge className="bg-slate-800 text-slate-300">{u.actions_count || 0}</Badge></td>
                <td className="p-2 text-[11px] text-slate-400 max-w-[200px] truncate">
                  {u.last_action ? `${u.last_action.action} · ${u.last_action.created_at ? new Date(u.last_action.created_at).toLocaleDateString() : ""}` : "—"}
                </td>
                <td className="p-2 text-center flex gap-1 justify-center">
                  <button onClick={() => openActivity(u)} className="text-slate-400 hover:text-yellow-400 p-1" title={t("عرض النشاط", "View activity")} data-testid={`team-activity-${u.user_id}`}><Activity size={14} /></button>
                  {me.can.manage_admins && !isMe && u.role !== "owner" && (
                    <button onClick={() => transfer(u)} className="text-slate-400 hover:text-yellow-300 p-1" title={t("نقل الملكية", "Transfer ownership")} data-testid={`team-transfer-${u.user_id}`}><ArrowRightLeft size={14} /></button>
                  )}
                  {me.can.manage_admins && !isMe && !isLastOwn && (
                    <button onClick={() => remove(u)} className="text-slate-400 hover:text-red-400 p-1" title={t("إزالة", "Remove")} data-testid={`team-remove-${u.user_id}`}><Trash2 size={14} /></button>
                  )}
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      {/* Activity drawer */}
      {activityFor && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setActivityFor(null)} data-testid="team-activity-modal">
          <div className="glass-card rounded-2xl p-5 max-w-2xl w-full max-h-[80vh] overflow-y-auto space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold flex items-center gap-2"><Activity className="text-yellow-400" size={18} />{t("نشاط:", "Activity:")} {activityFor.name}</h3>
            {activityFor.rows.length === 0 ? (
              <div className="text-center text-slate-400 py-8 text-sm">{t("لا توجد إجراءات لهذا العضو بعد", "No actions by this member yet")}</div>
            ) : (
              <div className="space-y-2">
                {activityFor.rows.map((a) => (
                  <div key={a.action_id} className="bg-slate-900/40 rounded-xl p-2.5 border border-slate-800 text-xs">
                    <div className="flex items-center justify-between">
                      <Badge className="bg-yellow-500/20 text-yellow-300">{a.action}</Badge>
                      <span className="text-slate-500 text-[10px]">{a.created_at ? new Date(a.created_at).toLocaleString() : ""}</span>
                    </div>
                    {a.target && <div className="text-slate-500 mt-1 font-mono text-[10px] truncate">target: {a.target}</div>}
                    {a.note && <div className="text-slate-300 mt-1">{a.note}</div>}
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end"><Button onClick={() => setActivityFor(null)} variant="outline" className="border-slate-700 text-slate-300" data-testid="team-activity-close">{t("إغلاق", "Close")}</Button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Moderation Log ───
function ModerationTab({ data, t }) {
  const rows = data || [];
  return rows.length === 0
    ? <div className="glass-card rounded-2xl p-10 text-center text-slate-400 text-sm" data-testid="moderation-empty">{t("لا توجد إجراءات بعد", "No moderation actions yet")}</div>
    : (
      <div className="glass-card rounded-2xl overflow-hidden" data-testid="moderation-tab">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/60 text-slate-400 text-[11px]">
            <tr>
              <th className="p-2 text-right">{t("التاريخ", "Time")}</th>
              <th className="p-2 text-right">{t("الإجراء", "Action")}</th>
              <th className="p-2 text-right">{t("بواسطة", "By")}</th>
              <th className="p-2 text-right">{t("الهدف", "Target")}</th>
              <th className="p-2 text-right">{t("ملاحظة", "Note")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.action_id} className="border-t border-slate-800" data-testid={`mod-row-${a.action_id}`}>
                <td className="p-2 text-slate-400 text-[11px]">{a.created_at ? new Date(a.created_at).toLocaleString() : ""}</td>
                <td className="p-2 text-white"><Badge className="bg-yellow-500/20 text-yellow-300">{a.action}</Badge></td>
                <td className="p-2 text-slate-300">{a.by_name || "—"}<div className="text-[10px] text-slate-500">{a.by_role}</div></td>
                <td className="p-2 text-slate-400 text-[11px] font-mono">{a.target || "—"}</td>
                <td className="p-2 text-slate-300 text-[11px]">{a.note || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
}

// ─── Reports ───
function ReportsTab({ data, t, token }) {
  const resolve = async (rid) => { await axios.post(`${API}/admin/reports/${rid}/resolve`, {}, auth(token)); toast.success(t("تم", "Done")); window.location.reload(); };
  const rows = data || [];
  return (
    <div className="space-y-2" data-testid="reports-tab">
      {rows.length === 0 ? <div className="text-slate-400 text-sm py-10 text-center">{t("لا توجد بلاغات مفتوحة", "No open reports")}</div> :
        rows.map((r) => (
          <div key={r.report_id} className="glass-card rounded-xl p-3 flex items-center gap-3" data-testid={`report-${r.report_id}`}>
            <AlertTriangle className="text-red-400" size={18} />
            <div className="flex-1">
              <div className="text-sm text-white">{r.reason}</div>
              <div className="text-[11px] text-slate-400">msg: {r.message_id} · {new Date(r.created_at).toLocaleString()}</div>
            </div>
            <Button onClick={() => resolve(r.report_id)} size="sm" className="btn-primary" data-testid={`report-resolve-${r.report_id}`}><CheckCircle2 size={12} className="ml-1" />{t("حل", "Resolve")}</Button>
          </div>
        ))}
    </div>
  );
}

// ─── Contact ───
function ContactTab({ data, t }) {
  const rows = data || [];
  return rows.length === 0 ? <div className="glass-card rounded-2xl p-10 text-center text-slate-400 text-sm">{t("لا توجد رسائل تواصل بعد", "No contact messages yet")}</div> :
    <div className="space-y-2" data-testid="contact-tab">
      {rows.map((m) => (
        <div key={m.message_id || m._id} className="glass-card rounded-xl p-3">
          <div className="flex justify-between"><span className="text-white font-semibold">{m.name}</span><Badge className="bg-slate-800 text-slate-300">{m.status || "new"}</Badge></div>
          <div className="text-xs text-slate-400 mt-1">{m.email} · {m.subject}</div>
          <div className="text-sm text-slate-200 mt-2">{m.message}</div>
        </div>
      ))}
    </div>;
}

// ─── Videos ───
function VideosTab({ data, me, t, token, reload }) {
  const canPublish  = !!me?.can?.video_publish;
  const canApprove  = !!me?.can?.video_approve;
  const canPin      = !!me?.can?.video_pin;
  const canReorder  = !!me?.can?.video_reorder;
  const canDelete   = !!me?.can?.video_delete_any;
  const canEditAny  = !!me?.can?.video_edit_any;
  const isContrib   = me?.role === "video_contributor";

  const [form, setForm] = useState({
    title: "", description: "", thumbnail_url: "", category: "homepage", placement: "",
    source_type: "link", source_url: "", status: "draft", sort_order: 0,
    duration_seconds: null, file_size: null,
  });
  const [rows,     setRows]     = useState([]);
  const [pending,  setPending]  = useState([]);
  const [pendingLoaded, setPendingLoaded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadCancel, setUploadCancel] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => { setRows(data || []); }, [data]);

  useEffect(() => {
    if (!canApprove) { setPendingLoaded(true); return; }
    axios.get(`${API}/admin/videos/pending`, auth(token))
      .then((r) => { setPending(r.data || []); setPendingLoaded(true); })
      .catch(() => setPendingLoaded(true));
  }, [canApprove, token]);

  const refreshAll = async () => {
    const [r1, r2] = await Promise.all([
      axios.get(`${API}/admin/videos`, auth(token)),
      canApprove ? axios.get(`${API}/admin/videos/pending`, auth(token)) : Promise.resolve({ data: [] }),
    ]);
    setRows(r1.data || []);
    setPending(r2.data || []);
  };

  // Probe file → duration + thumbnail
  const probeVideo = (file) => new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      v.preload = "metadata"; v.src = url; v.muted = true;
      let done = false;
      const finish = (dur, thumb) => { if (done) return; done = true; URL.revokeObjectURL(url); resolve({ duration_seconds: dur || 0, file_size: file.size, thumb }); };
      v.onloadedmetadata = () => {
        const dur = Math.round(v.duration || 0);
        v.currentTime = Math.min(1, Math.max(0, dur / 4));
        v.onseeked = () => {
          const c = document.createElement("canvas");
          const ratio = v.videoHeight / v.videoWidth || 0.5625;
          c.width = 320; c.height = Math.round(320 * ratio);
          try { c.getContext("2d").drawImage(v, 0, 0, c.width, c.height); finish(dur, c.toDataURL("image/jpeg", 0.7)); }
          catch { finish(dur, null); }
        };
        setTimeout(() => finish(dur, null), 5000);
      };
      v.onerror = () => finish(0, null);
    } catch { resolve({ duration_seconds: 0, file_size: file.size, thumb: null }); }
  });

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploadingFile({ name: file.name, size: file.size });
    setProgress(0);
    const meta = await probeVideo(file);
    setForm((f) => ({ ...f, duration_seconds: meta.duration_seconds, file_size: meta.file_size, thumbnail_url: f.thumbnail_url || (meta.thumb || "") }));
    const ctrl = new AbortController();
    setUploadCancel(() => ctrl);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/videos/upload`, fd, {
        headers: { ...auth(token).headers, "Content-Type": "multipart/form-data" },
        signal: ctrl.signal,
        onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
      });
      setForm((f) => ({ ...f, source_type: "upload", source_url: r.data.path, file_size: r.data.size || meta.file_size }));
      toast.success(t("تم رفع الفيديو — احفظه الآن", "Uploaded — save the video now"));
    } catch (e) {
      if (axios.isCancel?.(e) || e?.name === "CanceledError") toast.info(t("تم إلغاء الرفع", "Upload canceled"));
      else toast.error(e?.response?.data?.detail || t("فشل الرفع", "Upload failed"));
    } finally { setUploadCancel(null); setUploadingFile(null); setProgress(0); }
  };

  const cancelUpload = () => uploadCancel?.abort();

  const handleThumbUpload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await axios.post(`${API}/uploads/image`, fd, { headers: { ...auth(token).headers, "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, thumbnail_url: `${API}/uploads/${r.data.path}` }));
      toast.success(t("تم رفع الصورة المصغرة", "Thumbnail uploaded"));
    } catch { toast.error(t("فشل رفع الصورة", "Image upload failed")); }
  };

  const create = async () => {
    if (!form.title || !form.source_url) { toast.error(t("العنوان والمصدر مطلوبان", "Title + source required")); return; }
    try {
      const r = await axios.post(`${API}/admin/videos`, form, auth(token));
      toast.success(r.data?.status === "pending_approval"
        ? t("تم الإرسال للموافقة", "Submitted for approval")
        : t("تم الإضافة", "Created"));
      setForm({ title: "", description: "", thumbnail_url: "", category: "homepage", placement: "",
                source_type: "link", source_url: "", status: "draft", sort_order: 0, duration_seconds: null, file_size: null });
      refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || t("فشل", "Failed")); }
  };

  const del = async (id) => {
    if (!canDelete) { toast.error(t("غير مسموح", "Not allowed")); return; }
    if (!window.confirm(t("حذف هذا الفيديو؟", "Delete this video?"))) return;
    await axios.delete(`${API}/admin/videos/${id}`, auth(token));
    setRows((xs) => xs.filter((v) => v.video_id !== id));
    toast.success(t("تم الحذف", "Deleted"));
  };

  const updateStatus = async (v, status) => {
    try {
      const r = await axios.put(`${API}/admin/videos/${v.video_id}`, { ...v, status }, auth(token));
      setRows((xs) => xs.map((x) => x.video_id === v.video_id ? { ...x, status: r.data?.status || status } : x));
      toast.success(t("تم التحديث", "Updated"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("فشل", "Failed")); }
  };

  const approve = async (id) => {
    try { await axios.post(`${API}/admin/videos/${id}/approve`, {}, auth(token)); toast.success(t("تمت الموافقة ونشر الفيديو", "Approved & published")); refreshAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("فشل", "Failed")); }
  };
  const sendReject = async () => {
    try { await axios.post(`${API}/admin/videos/${rejectFor.video_id}/reject`, { reason: rejectReason }, auth(token)); toast.success(t("تم الرفض", "Rejected")); setRejectFor(null); setRejectReason(""); refreshAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("فشل", "Failed")); }
  };
  const togglePin = async (id) => {
    try { const r = await axios.post(`${API}/admin/videos/${id}/pin`, {}, auth(token)); toast.success(r.data.pinned ? t("تم التثبيت", "Pinned") : t("تم إلغاء التثبيت", "Unpinned")); refreshAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("فشل", "Failed")); }
  };

  const onDragStart = (i) => { if (canReorder) setDragIdx(i); };
  const onDragOver  = (e) => e.preventDefault();
  const onDrop      = async (i) => {
    if (!canReorder || dragIdx === null || dragIdx === i) return;
    const next = [...rows]; const [moved] = next.splice(dragIdx, 1); next.splice(i, 0, moved);
    const withOrder = next.map((v, idx) => ({ ...v, sort_order: idx }));
    setRows(withOrder); setDragIdx(null);
    try {
      await axios.post(`${API}/admin/videos/reorder`, { items: withOrder.map((v) => ({ video_id: v.video_id, sort_order: v.sort_order })) }, auth(token));
      toast.success(t("تم إعادة الترتيب", "Reordered"));
    } catch { toast.error(t("فشل الحفظ", "Save failed")); }
  };

  const inputCls = "bg-slate-900 text-white border border-slate-700 rounded px-2 py-1.5 text-sm w-full";

  return (
    <div className="space-y-3" data-testid="videos-tab">
      {/* Role banner */}
      <div className={`rounded-xl px-3 py-2 text-[11px] inline-flex items-center gap-2 ${isContrib ? "bg-blue-500/15 text-blue-200 border border-blue-500/30" : "bg-yellow-500/10 text-yellow-200 border border-yellow-500/30"}`} data-testid="videos-role-banner">
        {isContrib ? <Video size={12} /> : <Crown size={12} />}
        <span>{isContrib
          ? t("دور: مساهم فيديو — ارفع بأي حجم. لن يظهر علناً حتى يوافق المالك أو الأدمن.", "Role: Video Contributor — upload freely; videos appear only after Owner/Admin approval.")
          : `${t("الدور:", "Role:")} ${me?.role} · ${t("صلاحيات كاملة على Arab Sanad TV", "Full Arab Sanad TV powers")}`}</span>
      </div>

      {/* Pending Approval queue */}
      {canApprove && (
        <div className="glass-card rounded-2xl p-3 space-y-2" data-testid="videos-pending-queue">
          <h3 className="text-sm font-bold text-white flex items-center gap-2"><Hourglass size={14} className="text-amber-400" />{t("بانتظار الموافقة", "Pending approval")}
            <Badge className="bg-amber-500/20 text-amber-300 text-[10px]">{pending.length}</Badge></h3>
          {!pendingLoaded ? <div className="text-slate-400 text-xs">{t("جاري التحميل…", "Loading…")}</div> :
           pending.length === 0 ? <div className="text-slate-500 text-xs py-2" data-testid="pending-empty">{t("لا توجد فيديوهات بانتظار الموافقة", "No videos awaiting approval")}</div> :
           pending.map((v) => (
            <div key={v.video_id} className="flex items-center gap-3 bg-slate-900/40 rounded-xl p-2.5 border border-amber-500/30" data-testid={`pending-${v.video_id}`}>
              {v.thumbnail_url
                ? <img src={v.thumbnail_url.startsWith("http") || v.thumbnail_url.startsWith("data:") ? v.thumbnail_url : `${API}/uploads/${v.thumbnail_url}`} alt="" className="w-16 h-10 object-cover rounded" />
                : <div className="w-16 h-10 bg-slate-800 rounded flex items-center justify-center"><Video size={14} className="text-yellow-500" /></div>}
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{v.title}</div>
                <div className="text-[10px] text-slate-400 truncate">{v.category} · {fmtDuration(v.duration_seconds)} · {fmtBytes(v.file_size)} · {v.created_by_role || "—"}</div>
              </div>
              <Button onClick={() => approve(v.video_id)} className="btn-primary text-xs h-8" data-testid={`pending-approve-${v.video_id}`}><CheckCircle2 size={12} className="ml-1" />{t("موافقة", "Approve")}</Button>
              <Button onClick={() => { setRejectFor(v); setRejectReason(""); }} variant="outline" className="border-red-500/40 text-red-300 text-xs h-8" data-testid={`pending-reject-${v.video_id}`}><XCircle size={12} className="ml-1" />{t("رفض", "Reject")}</Button>
            </div>))}
        </div>
      )}

      {/* Upload / create form */}
      <div className="glass-card rounded-2xl p-4 space-y-2">
        <h3 className="text-sm font-bold text-white flex items-center gap-1"><Plus size={14} className="text-yellow-500" />{t("إضافة فيديو جديد", "Add new video")}</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          <input className={inputCls} placeholder={t("العنوان", "Title")} value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="video-title" />
          <select className={inputCls} value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="video-category">
            <option value="homepage">Homepage</option><option value="resume_center">Resume Center</option>
            <option value="jobs">Jobs</option><option value="housing">Housing</option>
            <option value="discover">Discover</option><option value="tutorials">Tutorials</option><option value="marketing">Marketing</option>
          </select>
          <select className={inputCls} value={form.source_type} onChange={(e) => setForm({...form, source_type: e.target.value, source_url: ""})} data-testid="video-source-type">
            <option value="link">YouTube / Vimeo link</option><option value="upload">Direct upload (no size limit)</option>
          </select>
          {form.source_type === "link" ? (
            <input className={inputCls} placeholder="https://youtube.com/watch?v=… or https://vimeo.com/…" value={form.source_url} onChange={(e) => setForm({...form, source_url: e.target.value})} data-testid="video-source-url" />
          ) : (
            <label className="cursor-pointer bg-slate-900 border border-dashed border-slate-700 rounded px-2 py-1.5 text-sm text-slate-300 hover:border-yellow-500/50 inline-flex items-center justify-center gap-2" data-testid="video-upload-label">
              {uploadingFile ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} className="text-yellow-500" />}
              <span className="truncate">{form.source_url ? form.source_url.split("/").pop() : t("ارفع فيديو (بأي حجم)", "Upload video (any size)")}</span>
              <input type="file" accept="video/mp4,video/webm,video/quicktime,video/ogg,video/x-matroska" className="hidden" onChange={(e) => handleFileUpload(e.target.files?.[0])} data-testid="video-upload-input" />
            </label>
          )}
          <div className="flex gap-1 items-center">
            <input className={inputCls} placeholder={t("صورة Thumbnail (رابط)", "Thumbnail URL")} value={form.thumbnail_url} onChange={(e) => setForm({...form, thumbnail_url: e.target.value})} />
            <label className="cursor-pointer bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-300 hover:border-yellow-500/40" data-testid="thumb-upload-label" title={t("ارفع صورة مصغرة", "Upload thumbnail")}>
              <Upload size={12} />
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => handleThumbUpload(e.target.files?.[0])} />
            </label>
          </div>
          <select className={inputCls} value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} data-testid="video-status">
            <option value="draft">{t("مسودة", "Draft")}</option>
            {canPublish && <option value="published">{t("منشور", "Published")}</option>}
            {canPublish && <option value="hidden">{t("مخفي", "Hidden")}</option>}
            {!canPublish && <option value="pending_approval">{t("إرسال للموافقة", "Submit for approval")}</option>}
          </select>
        </div>

        {uploadingFile && (
          <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-3 space-y-2" data-testid="upload-progress-panel">
            <div className="flex items-center gap-2 text-xs">
              <Loader2 size={14} className="animate-spin text-yellow-500" />
              <span className="text-white truncate flex-1">{uploadingFile.name}</span>
              <span className="text-slate-400">{fmtBytes(uploadingFile.size)}</span>
              <button onClick={cancelUpload} className="text-red-400 hover:text-red-300" title={t("إلغاء", "Cancel")} data-testid="upload-cancel-btn"><X size={14} /></button>
            </div>
            <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden"><div className="bg-yellow-500 h-2 transition-all" style={{width: `${progress}%`}} data-testid="upload-progress-bar" /></div>
            <div className="text-[11px] text-slate-400 flex justify-between"><span>{progress}%</span><span>{progress === 100 ? t("اكتمل — في انتظار التأكيد", "Done — finalizing") : t("جاري الرفع…", "Uploading…")}</span></div>
          </div>
        )}

        {(form.source_url && form.source_type === "upload" && !uploadingFile) && (
          <div className="flex gap-3 bg-slate-900/50 rounded-xl p-2 border border-slate-800" data-testid="upload-meta-preview">
            {form.thumbnail_url ? (
              <img src={form.thumbnail_url} alt="thumb" className="w-24 h-14 object-cover rounded border border-slate-700" />
            ) : (
              <div className="w-24 h-14 bg-slate-800 rounded flex items-center justify-center"><ImageIcon size={16} className="text-slate-600" /></div>
            )}
            <div className="text-[11px] text-slate-300 space-y-0.5">
              <div className="inline-flex items-center gap-1"><Clock size={11} />{fmtDuration(form.duration_seconds)}</div>
              <div className="text-slate-400">{fmtBytes(form.file_size)}</div>
              <div className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 size={11} />{t("جاهز للحفظ", "Ready to save")}</div>
            </div>
          </div>
        )}

        <textarea className={inputCls + " resize-none"} rows={2} placeholder={t("وصف الفيديو", "Description")} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="video-description" />
        <Button onClick={create} className="btn-primary" data-testid="video-create-btn">
          <Plus size={14} className="ml-1" />
          {isContrib && form.status === "pending_approval" ? t("إرسال للموافقة", "Submit for approval") : t("أضف الفيديو", "Add video")}
        </Button>
      </div>

      {/* All videos list */}
      <div className="glass-card rounded-2xl p-3 space-y-2">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <Video size={14} className="text-yellow-500" />
          {isContrib ? t("فيديوهاتي", "My videos") : t("الفيديوهات", "All videos")} ({rows.length})
          {canReorder && <span className="text-[10px] text-slate-500 ml-auto inline-flex items-center gap-1"><GripVertical size={11} />{t("اسحب لإعادة الترتيب", "Drag to reorder")}</span>}
        </h3>
        {rows.length === 0 ? <div className="text-slate-400 text-sm py-6 text-center" data-testid="videos-empty">{t("لا توجد فيديوهات بعد", "No videos yet")}</div> :
          rows.map((v, i) => (
            <div key={v.video_id}
              draggable={canReorder}
              onDragStart={() => onDragStart(i)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(i)}
              className={`flex flex-wrap items-center gap-3 bg-slate-900/40 rounded-xl p-2.5 border ${dragIdx === i ? "border-yellow-500/60" : v.pinned ? "border-yellow-500/40" : "border-slate-800"} hover:border-slate-700 ${canReorder ? "cursor-move" : ""} transition`}
              data-testid={`video-${v.video_id}`}
            >
              {canReorder && <GripVertical size={14} className="text-slate-600 flex-shrink-0" />}
              {v.thumbnail_url
                ? <img src={v.thumbnail_url.startsWith("http") || v.thumbnail_url.startsWith("data:") ? v.thumbnail_url : `${API}/uploads/${v.thumbnail_url}`} alt="" className="w-16 h-10 object-cover rounded flex-shrink-0" />
                : <Video size={16} className="text-yellow-500 flex-shrink-0" />}
              <div className="flex-1 min-w-[180px]">
                <div className="text-sm text-white truncate inline-flex items-center gap-1">{v.pinned && <Pin size={11} className="text-yellow-400" />}{v.title}</div>
                <div className="text-[10px] text-slate-400 truncate">{v.category} · {v.source_type} · {fmtDuration(v.duration_seconds)} · {fmtBytes(v.file_size)}</div>
                {v.status === "rejected" && v.rejected_reason && <div className="text-[10px] text-red-300 mt-0.5">⚠ {v.rejected_reason}</div>}
              </div>
              <div className="hidden sm:flex items-center gap-3 text-[11px] text-slate-400">
                <span className="inline-flex items-center gap-1" title={t("مشاهدات", "Views")}><Eye size={12} />{v.views || 0}</span>
                <span className="inline-flex items-center gap-1" title={t("نقرات", "Clicks")}><MousePointer size={12} />{v.clicks || 0}</span>
              </div>
              {(canEditAny || v.created_by === me?.user_id) ? (
                <select value={v.status} onChange={(e) => updateStatus(v, e.target.value)} className={`text-xs rounded px-2 py-1 border border-slate-700 ${statusBadgeCls(v.status)}`} data-testid={`video-status-${v.video_id}`}>
                  <option value="draft">Draft</option>
                  <option value="pending_approval">Pending</option>
                  {canPublish && <option value="published">Published</option>}
                  {canPublish && <option value="hidden">Hidden</option>}
                  {v.status === "rejected" && <option value="rejected">Rejected</option>}
                </select>
              ) : (
                <Badge className={`text-[10px] ${statusBadgeCls(v.status)}`}>{statusLabel(v.status, t)}</Badge>
              )}
              {canApprove && v.status === "pending_approval" && (
                <>
                  <button onClick={() => approve(v.video_id)} className="text-emerald-400 hover:text-emerald-300 p-1" title={t("موافقة", "Approve")} data-testid={`video-approve-${v.video_id}`}><CheckCircle2 size={14} /></button>
                  <button onClick={() => { setRejectFor(v); setRejectReason(""); }} className="text-red-400 hover:text-red-300 p-1" title={t("رفض", "Reject")} data-testid={`video-reject-${v.video_id}`}><XCircle size={14} /></button>
                </>
              )}
              {canPin && (
                <button onClick={() => togglePin(v.video_id)} className={`p-1 ${v.pinned ? "text-yellow-400" : "text-slate-400 hover:text-yellow-400"}`} title={v.pinned ? t("إلغاء التثبيت", "Unpin") : t("تثبيت", "Pin")} data-testid={`video-pin-${v.video_id}`}><Pin size={14} /></button>
              )}
              {canDelete && (
                <button onClick={() => del(v.video_id)} className="text-slate-400 hover:text-red-400 p-1" data-testid={`video-delete-${v.video_id}`}><Trash2 size={14} /></button>
              )}
            </div>
          ))}
      </div>

      {/* Reject modal */}
      {rejectFor && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setRejectFor(null)} data-testid="video-reject-modal">
          <div className="glass-card rounded-2xl p-5 max-w-md w-full space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold flex items-center gap-2"><XCircle className="text-red-400" size={18} />{t("رفض الفيديو", "Reject video")}</h3>
            <div className="text-sm text-slate-300">{rejectFor.title}</div>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3}
              placeholder={t("سبب الرفض (سيظهر للمساهم)", "Reason (visible to contributor)")}
              className="w-full bg-slate-900 text-white border border-slate-700 rounded-lg p-2 text-sm resize-none"
              data-testid="reject-reason" />
            <div className="flex justify-end gap-2">
              <Button onClick={() => setRejectFor(null)} variant="outline" className="border-slate-700 text-slate-300" data-testid="reject-cancel">{t("إلغاء", "Cancel")}</Button>
              <Button onClick={sendReject} className="bg-red-600 hover:bg-red-700 text-white" data-testid="reject-confirm"><XCircle size={12} className="ml-1" />{t("تأكيد الرفض", "Reject")}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Media Approval Queue ───
// Admin queue for any photo/video uploaded against a marketplace listing
// (property / vehicle / provider / grocery / store). Each piece of media starts
// as `pending_review` and is hidden from the public surface until an admin approves it.
// Backend endpoints:
//   GET    /admin/media?status=pending_review   (default; switchable to "approved"/"rejected"/"all")
//   POST   /admin/media/{media_id}/approve
//   POST   /admin/media/{media_id}/reject   (body: { rejection_reason })
//   DELETE /admin/media/{media_id}
function MediaTab({ data, t, token, reload }) {
  const [filter, setFilter]   = useState("pending_review");
  const [busy, setBusy]       = useState(null);  // media_id currently being mutated
  const [rejectId, setRejectId] = useState(null); // media_id awaiting rejection reason
  const [rejectReason, setRejectReason] = useState("");

  // When the filter changes, reload the data for that subset.
  useEffect(() => {
    (async () => {
      try {
        const url = filter === "all" ? `${API}/admin/media` : `${API}/admin/media?status=${filter}`;
        const r   = await axios.get(url, auth(token));
        // Push into parent state via reload pattern — but we can also just stash locally.
        // We use a local stash via window since `data` is owned by parent.
        window.__mediaStash = r.data;
        // Force re-render by calling reload only when we're on the default tab.
        if (filter === "pending_review") reload && reload();
      } catch (e) { /* swallow */ }
    })();
  }, [filter, token, reload]);

  // Defensive: parent may briefly hand us non-array data during tab transitions; always coerce.
  const stash = Array.isArray(window.__mediaStash) ? window.__mediaStash : [];
  const baseData = Array.isArray(data) ? data : [];
  const items = filter === "pending_review" ? baseData : stash;

  const approve = async (id) => {
    setBusy(id);
    try {
      await axios.post(`${API}/admin/media/${id}/approve`, {}, auth(token));
      toast.success(t("تمت الموافقة", "Approved"));
      await reload();
    } catch (e) {
      toast.error(t("تعذّر التنفيذ", "Action failed"));
    } finally { setBusy(null); }
  };
  const reject = async (id) => {
    setBusy(id);
    try {
      await axios.post(`${API}/admin/media/${id}/reject`, { rejection_reason: rejectReason || "" }, auth(token));
      toast.success(t("تم الرفض", "Rejected"));
      setRejectId(null);
      setRejectReason("");
      await reload();
    } catch (e) {
      toast.error(t("تعذّر التنفيذ", "Action failed"));
    } finally { setBusy(null); }
  };
  const remove = async (id) => {
    if (!window.confirm(t("حذف نهائي؟ لا يمكن التراجع.", "Delete permanently? This cannot be undone."))) return;
    setBusy(id);
    try {
      await axios.delete(`${API}/admin/media/${id}`, auth(token));
      toast.success(t("تم الحذف", "Deleted"));
      await reload();
    } catch (e) {
      toast.error(t("تعذّر الحذف", "Delete failed"));
    } finally { setBusy(null); }
  };

  const statusCls = (s) => ({
    pending_review: "bg-amber-500/20 text-amber-300",
    approved:       "bg-emerald-500/20 text-emerald-300",
    rejected:       "bg-red-500/20 text-red-300",
  }[s] || "bg-slate-800 text-slate-300");

  return (
    <div className="space-y-4" data-testid="admin-media-tab">
      {/* Filter pills */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: "pending_review", ar: "بانتظار المراجعة", en: "Pending" },
          { id: "approved",       ar: "موافَق عليه",      en: "Approved" },
          { id: "rejected",       ar: "مرفوض",            en: "Rejected" },
          { id: "all",            ar: "الكل",             en: "All" },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${filter === f.id ? "bg-yellow-500 text-black font-bold border-yellow-500" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}
            data-testid={`media-filter-${f.id}`}
          >
            {t(f.ar, f.en)}
          </button>
        ))}
        <span className="text-xs text-slate-400 ml-auto self-center">{items.length} {t("عنصر", "items")}</span>
      </div>

      {items.length === 0 ? (
        <div className="glass-card rounded-2xl p-10 text-center text-slate-400 text-sm">
          {t("لا يوجد ملفات للمراجعة في هذا التصنيف.", "No media in this bucket.")}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((m) => (
            <div key={m.media_id} className="glass-card rounded-xl overflow-hidden flex flex-col" data-testid={`media-${m.media_id}`}>
              {/* Preview */}
              <div className="aspect-video bg-slate-900 grid place-items-center overflow-hidden">
                {m.media_type === "video" ? (
                  <video src={`${API}/uploads/${(m.url || "").replace(/^\/+/, "")}`} className="w-full h-full object-cover" controls preload="metadata" />
                ) : (
                  <img src={`${API}/uploads/${(m.url || "").replace(/^\/+/, "")}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                )}
              </div>
              {/* Meta */}
              <div className="p-3 space-y-2 flex-1 flex flex-col">
                <div className="flex items-center justify-between gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${statusCls(m.status)}`}>
                    {m.status === "pending_review" ? t("بانتظار المراجعة", "Pending") :
                     m.status === "approved"       ? t("موافَق عليه", "Approved") :
                                                     t("مرفوض", "Rejected")}
                  </span>
                  <span className="text-[10px] text-slate-500">{m.media_type === "video" ? t("فيديو", "video") : t("صورة", "image")}</span>
                </div>
                <div className="text-[11px] text-slate-400 leading-tight">
                  <div><span className="text-slate-500">{t("نوع الإعلان:", "Listing:")} </span><span className="text-slate-200">{m.listing_type}</span></div>
                  <div className="truncate" title={m.listing_id}><span className="text-slate-500">{t("الإعلان:", "ID:")} </span><span className="text-slate-200">{m.listing_id}</span></div>
                  <div className="truncate" title={m.owner_id}><span className="text-slate-500">{t("المالك:", "Owner:")} </span><span className="text-slate-200">{m.owner_id}</span></div>
                </div>
                {m.rejection_reason && (
                  <div className="text-[11px] text-red-300 bg-red-500/10 rounded px-2 py-1">{m.rejection_reason}</div>
                )}
                {rejectId === m.media_id ? (
                  <div className="space-y-2 mt-auto">
                    <Input
                      placeholder={t("سبب الرفض (اختياري)", "Rejection reason (optional)")}
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      className="bg-slate-900 border-slate-700 text-white text-xs"
                      data-testid={`reject-reason-${m.media_id}`}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => reject(m.media_id)} disabled={busy === m.media_id} className="bg-red-500 hover:bg-red-600 text-white text-xs flex-1" data-testid={`media-reject-confirm-${m.media_id}`}>
                        {t("تأكيد الرفض", "Confirm reject")}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setRejectId(null); setRejectReason(""); }} className="border-slate-700 text-white text-xs">
                        {t("إلغاء", "Cancel")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2 pt-1 mt-auto">
                    {m.status !== "approved" && (
                      <Button size="sm" onClick={() => approve(m.media_id)} disabled={busy === m.media_id} className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs flex-1" data-testid={`media-approve-${m.media_id}`}>
                        <CheckCircle2 size={12} className="mr-1" /> {t("موافقة", "Approve")}
                      </Button>
                    )}
                    {m.status !== "rejected" && (
                      <Button size="sm" onClick={() => setRejectId(m.media_id)} disabled={busy === m.media_id} variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10 text-xs flex-1" data-testid={`media-reject-${m.media_id}`}>
                        <XCircle size={12} className="mr-1" /> {t("رفض", "Reject")}
                      </Button>
                    )}
                    <Button size="sm" onClick={() => remove(m.media_id)} disabled={busy === m.media_id} className="bg-slate-700 hover:bg-slate-600 text-white text-xs" data-testid={`media-delete-${m.media_id}`}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Payments ───
function PaymentsTab({ data, t }) {
  const rows = data || [];
  return rows.length === 0 ? <div className="glass-card rounded-2xl p-10 text-center text-slate-400 text-sm">{t("لا توجد مدفوعات بعد", "No payments yet — Stripe Checkout will populate this list once live")}</div> :
    <div className="space-y-2">
      {rows.map((p, i) => (
        <div key={i} className="glass-card rounded-xl p-3 flex items-center gap-3">
          <CreditCard size={14} className="text-yellow-500" />
          <div className="flex-1"><div className="text-sm text-white">${p.amount} · {p.status}</div><div className="text-[11px] text-slate-400">{p.created_at}</div></div>
        </div>
      ))}
    </div>;
}

// ─── Analytics ───
function AnalyticsTab({ data, t }) {
  if (!data) return null;
  return (
    <div className="space-y-3" data-testid="analytics-tab">
      <StatCard label={t("إجمالي السير المنشأة", "Total resumes")} value={data.total_resumes} />
      <div className="glass-card rounded-2xl p-4">
        <h3 className="text-sm font-bold text-white mb-2 inline-flex items-center gap-1"><BarChart3 size={14} className="text-yellow-500" />{t("القوالب الأكثر استخداماً", "Most used templates")}</h3>
        {(data.top_templates || []).map((tt) => (
          <div key={tt.template} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-800">
            <span className="text-slate-300">{tt.template}</span>
            <Badge className="bg-yellow-500/20 text-yellow-300">{tt.count}</Badge>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── System Health ───
function HealthTab({ data, t }) {
  if (!data) return null;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="health-tab">
      {Object.entries(data).map(([svc, info]) => (
        <div key={svc} className="glass-card rounded-2xl p-4 flex items-center justify-between" data-testid={`health-${svc}`}>
          <div>
            <div className="text-sm font-semibold text-white capitalize">{svc.replace(/_/g, " ")}</div>
            <div className="text-[10px] text-slate-500">{info.configured ? t("مُعدّ", "configured") : t("غير مُعدّ", "not configured")}</div>
          </div>
          {info.status === "ok"
            ? <CheckCircle2 className="text-emerald-400" size={20} />
            : <XCircle className="text-red-400" size={20} />}
        </div>
      ))}
    </div>
  );
}
